import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { AriaOrb } from "../components/AriaOrb";
import { InterviewCompare } from "../components/InterviewCompare";
import { AdvisorThreads } from "../components/AdvisorThreads";
import {
  Mic2, Plus, Loader2, ChevronLeft, Send, Youtube, StickyNote, BookOpen, FileText, TriangleAlert, CheckCircle2,
  Mic, Square, Volume2, VolumeX, Target, TrendingUp, Sparkles, ArrowRight, CalendarClock, Sunrise, FileDown, Mail, X, MessageSquare, Link2, Reply, CornerDownRight, GitCompare, CheckSquare, Square as SquareIcon, Users,
} from "lucide-react";
import { toast } from "sonner";

const SOURCE_ICON = { video: Youtube, note: StickyNote, turbolearn: BookOpen, paper: FileText };
const LEVEL_LABEL = ["Unseen", "Exposed", "Familiar", "Understands", "Can apply", "Can teach"];

export default function Interviews() {
  const navigate = useNavigate();
  const [interviews, setInterviews] = useState([]);
  const [weak, setWeak] = useState([]);
  const [creating, setCreating] = useState(false);
  const [target, setTarget] = useState("");
  const [voice, setVoice] = useState(false);
  const [busy, setBusy] = useState(null);
  const [unread, setUnread] = useState({});
  const [compare, setCompare] = useState(false);
  const [threads, setThreads] = useState(false);
  const [picked, setPicked] = useState([]);
  const togglePick = (id) => setPicked(p => p.includes(id) ? p.filter(x => x !== id) : p.length < 2 ? [...p, id] : [p[1], id]);

  useEffect(() => {
    (async () => {
      try {
        const [i, d, u] = await Promise.all([api.get("/interviews"), api.get("/home/dashboard"), api.get("/advisor/unread")]);
        setInterviews(i.data);
        setWeak(d.data.weak_concepts || []);
        setUnread(u.data.by_interview || {});
      } catch (err) { console.error("[ARIA]", err); }
    })();
  }, []);

  const start = async (body, key) => {
    if (busy) return;
    setBusy(key);
    try {
      const { data } = await api.post("/interviews", { ...body, mode: voice ? "voice" : "text" });
      navigate(`/app/interviews/${data.interview_id}`);
    } catch { toast.error("Could not start interview"); setBusy(null); }
  };

  return (
    <div className="h-screen overflow-y-auto p-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl mb-1">Interviews</h1>
            <p className="text-sm text-[var(--aria-text-muted)]">Adaptive mock interviews — ARIA probes weak spots and teaches gaps live.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setThreads(!threads)} data-testid="advisor-threads-toggle"
              className={`btn px-3 py-2 text-sm flex items-center gap-2 border ${threads ? "border-[var(--aria-primary)] text-[var(--aria-primary)] bg-[var(--aria-primary)]/10" : "border-[var(--aria-border)] text-[var(--aria-text-muted)] hover:text-[var(--aria-text)]"}`}>
              <Users size={14} /> Advisors{Object.values(unread).reduce((a, b) => a + b, 0) > 0 && <span className="text-[10px] px-1.5 rounded-full bg-emerald-400 text-[#030711]">{Object.values(unread).reduce((a, b) => a + b, 0)}</span>}
            </button>
            <button onClick={() => { setCompare(!compare); setPicked([]); }} data-testid="compare-toggle"
              className={`btn px-3 py-2 text-sm flex items-center gap-2 border ${compare ? "border-[var(--aria-primary)] text-[var(--aria-primary)] bg-[var(--aria-primary)]/10" : "border-[var(--aria-border)] text-[var(--aria-text-muted)] hover:text-[var(--aria-text)]"}`}>
              <GitCompare size={14} /> Compare
            </button>
            <button onClick={() => navigate("/app/warmup")} data-testid="interview-day-button"
              className="btn px-3 py-2 text-sm flex items-center gap-2 border border-[var(--aria-border)] text-[var(--aria-text-muted)] hover:text-[var(--aria-text)] hover:border-[var(--aria-primary)]">
              <Sunrise size={14} /> Interview day
            </button>
            <button onClick={() => setVoice(!voice)} data-testid="voice-mode-toggle"
              className={`btn px-3 py-2 text-sm flex items-center gap-2 border ${voice ? "border-[var(--aria-primary)] text-[var(--aria-primary)] bg-[var(--aria-primary)]/10" : "border-[var(--aria-border)] text-[var(--aria-text-muted)]"}`}>
              {voice ? <Volume2 size={14} /> : <VolumeX size={14} />} Voice {voice ? "on" : "off"}
            </button>
            <button onClick={() => setCreating(!creating)} data-testid="new-interview-button"
              className="btn btn-primary px-4 py-2 text-sm flex items-center gap-2"><Plus size={14} /> New interview</button>
          </div>
        </div>

        {weak.length > 0 && (
          <div className="mb-6" data-testid="quick-drills">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[var(--aria-text-muted)] mb-2 flex items-center gap-2">
              <Target size={12} className="text-[var(--aria-primary)]" /> Quick drills — your weakest concepts
            </div>
            <div className="flex flex-wrap gap-2">
              {weak.map(c => (
                <button key={c.concept} data-testid={`drill-${c.concept}`} disabled={!!busy}
                  onClick={() => start({ target: `Review: ${c.concept}`, topic_focus: [c.concept] }, c.concept)}
                  className="aria-pill aria-magnetic px-3 py-1.5 text-sm flex items-center gap-2 hover:border-[var(--aria-primary)] disabled:opacity-50">
                  {busy === c.concept ? <Loader2 size={12} className="animate-spin" /> : <Mic2 size={12} className="text-[var(--aria-primary)]" />}
                  {c.concept} <span className="text-[var(--aria-text-muted)] text-xs">· {LEVEL_LABEL[c.level]}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {creating && (
          <div className="aria-card p-5 mb-6 flex gap-3 items-end" data-testid="interview-create-form">
            <div className="flex-1">
              <label className="text-xs uppercase tracking-wider text-[var(--aria-text-muted)]">Target (role, company, or focus)</label>
              <input value={target} onChange={e => setTarget(e.target.value)} placeholder="e.g. Biophysics PhD interview"
                data-testid="interview-target-input"
                className="w-full mt-1 px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none focus:ring-2 focus:ring-[var(--aria-primary)] text-sm" />
            </div>
            <button onClick={() => start({ target: target || "General academic interview", topic_focus: [] }, "custom")} disabled={!!busy} data-testid="interview-start-button"
              className="btn btn-primary px-4 py-2 text-sm disabled:opacity-50 flex items-center gap-2">
              {busy === "custom" ? <Loader2 size={14} className="animate-spin" /> : <Mic2 size={14} />} Start
            </button>
          </div>
        )}

        {threads && <AdvisorThreads onClose={() => setThreads(false)} />}
        {compare && <InterviewCompare ids={picked} onClose={() => { setCompare(false); setPicked([]); }} />}

        <div className="space-y-3">
          {interviews.map(i => (
            <div key={i.interview_id} onClick={() => compare ? (i.debrief && togglePick(i.interview_id)) : navigate(`/app/interviews/${i.interview_id}`)} data-testid={`interview-card-${i.interview_id}`}
              className={`aria-card aria-magnetic p-5 cursor-pointer flex items-center justify-between gap-4 ${compare && !i.debrief ? "opacity-40" : ""} ${picked.includes(i.interview_id) ? "ring-glow-primary border-[var(--aria-primary)]" : ""}`}>
              {compare && (
                <span className="shrink-0 text-[var(--aria-primary)]" data-testid={`compare-pick-${i.interview_id}`}>
                  {picked.includes(i.interview_id) ? <CheckSquare size={18} /> : <SquareIcon size={18} className={i.debrief ? "" : "opacity-30"} />}
                </span>
              )}
              <div className="min-w-0 flex-1">
                <div className="font-display text-lg truncate">{i.target}</div>
                <div className="text-xs text-[var(--aria-text-muted)] mt-1">
                  {i.status} · {i.turns?.length || 0} turns · {(i.gaps_detected || []).length} gap(s) found
                  {i.debrief?.stats?.levels_gained > 0 && <span className="text-emerald-400"> · +{i.debrief.stats.levels_gained} mastery</span>}
                  {i.mode === "voice" && <span> · voice</span>}
                </div>
                {i.debrief?.summary && <div className="text-xs text-[var(--aria-text-muted)] mt-1.5 line-clamp-1">{i.debrief.summary}</div>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {unread[i.interview_id] > 0 && (
                  <span className="aria-pill aria-pill-success text-[10px] inline-flex items-center gap-1" data-testid={`unread-badge-${i.interview_id}`}>
                    <MessageSquare size={10} /> {unread[i.interview_id]} new
                  </span>
                )}
                <div className="aria-pill">{new Date(i.started_at).toLocaleDateString()}</div>
              </div>
            </div>
          ))}
          {interviews.length === 0 && !creating && (
            <div className="text-center py-16 text-[var(--aria-text-muted)]">No interviews yet — start your first mock interview above.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function useVoice() {
  const audioRef = useRef(null);
  const [speaking, setSpeaking] = useState(false);

  const stop = () => { audioRef.current?.pause(); audioRef.current = null; setSpeaking(false); };
  const speak = async (text) => {
    if (!text) return;
    stop();
    try {
      const { data } = await api.post("/tts", { text: text.slice(0, 1500) }, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const audio = new Audio(url);
      audioRef.current = audio;
      setSpeaking(true);
      audio.onended = () => { setSpeaking(false); URL.revokeObjectURL(url); };
      await audio.play();
    } catch { setSpeaking(false); }
  };
  useEffect(() => () => stop(), []);
  return { speak, stop, speaking };
}

function InterviewSession({ id }) {
  const navigate = useNavigate();
  const [interview, setInterview] = useState(null);
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  const [ending, setEnding] = useState(false);
  const [gapFlash, setGapFlash] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [voiceOn, setVoiceOn] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recorderRef = useRef(null);
  const spokenRef = useRef(0);
  const { speak, stop, speaking } = useVoice();

  const speakRef = useRef(speak);
  speakRef.current = speak;
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/interviews/${id}`);
      setInterview(data);
      spokenRef.current = data.turns.length;
      if (data.mode === "voice") {
        setVoiceOn(true);
        if (data.status === "active") speakRef.current(data.turns[data.turns.length - 1]?.content);
      }
    } catch { toast.error("Could not load interview"); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!interview) return;
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - new Date(interview.started_at).getTime()) / 1000)), 1000);
    return () => clearInterval(t);
  }, [interview]);

  const applyUpdate = (data) => {
    setInterview(data);
    if (data.gap_event) {
      setGapFlash(data.gap_event);
      setTimeout(() => setGapFlash(null), 6000);
    }
    const last = data.turns[data.turns.length - 1];
    if (voiceOn && last?.role === "aria" && data.turns.length > spokenRef.current) speak(last.content);
    spokenRef.current = data.turns.length;
  };

  const submit = async (text) => {
    const a = (text ?? answer).trim();
    if (!a || busy) return;
    setBusy(true);
    setAnswer("");
    stop();
    try {
      const { data } = await api.post(`/interviews/${id}/answer`, { answer: a });
      applyUpdate(data);
    } catch { toast.error("Failed to submit answer"); }
    finally { setBusy(false); }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];
      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunks, { type: "audio/webm" });
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "answer.webm");
          const { data } = await api.post("/stt", fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (data.text?.trim()) { setAnswer(data.text); await submit(data.text); }
          else toast.error("I didn't catch that — try again");
        } catch { toast.error("Transcription failed"); }
        finally { setTranscribing(false); }
      };
      stop();
      recorder.start();
      recorderRef.current = recorder;
      setRecording(true);
    } catch { toast.error("Microphone access denied"); }
  };
  const stopRecording = () => { recorderRef.current?.stop(); setRecording(false); };

  const end = async () => {
    setEnding(true);
    try { const { data } = await api.post(`/interviews/${id}/end`); setInterview(data); }
    catch { toast.error("Could not end interview"); }
    finally { setEnding(false); }
  };

  const replayQuestion = () => {
    const last = [...interview.turns].reverse().find(t => t.role === "aria");
    if (last) speak(last.content);
  };

  if (!interview) return <div className="h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[var(--aria-primary)]" /></div>;

  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");
  const currentConcept = [...interview.turns].reverse().find(t => t.role === "aria")?.concept;
  const orbState = recording ? "listening" : (busy || transcribing || ending) ? "thinking" : speaking ? "speaking" : interview.pending_gap ? "connecting" : "idle";

  return (
    <div className="h-screen overflow-y-auto bg-[#030711] text-white" data-testid="interview-session">
      <div className="max-w-3xl mx-auto p-8">
        <button onClick={() => navigate("/app/interviews")} data-testid="back-to-interviews" className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white mb-6">
          <ChevronLeft size={14} /> Back
        </button>

        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-[var(--aria-primary-glow)] font-semibold">ARIA Interview</div>
            <h1 className="font-display text-2xl">Dr. Shankar</h1>
          </div>
          <AriaOrb size={48} state={orbState} />
        </div>
        <div className="text-sm text-slate-400 mb-6">Adaptive interview session</div>
        <button data-testid="interview-shared-prep" onClick={() => navigate("/app/chat", { state: { interview_id: id, prompt: "Use what we worked on in Claude to prep me for this interview." } })}
          className="btn mb-5 px-3 py-2 text-sm border border-[var(--aria-border)] text-[var(--aria-primary)] inline-flex items-center gap-2"><MessageSquare size={14} />Prep with shared context</button>

        <div className="flex items-center gap-4 text-xs font-mono text-slate-400 mb-6 flex-wrap">
          <span>{interview.target}</span>
          <span>·</span>
          <span data-testid="interview-elapsed">{mm}:{ss}</span>
          <span>·</span>
          <span className="text-[var(--aria-primary-glow)]" data-testid="interview-current-concept">{currentConcept}</span>
          {interview.status === "completed" && <span className="aria-pill aria-pill-success">Completed</span>}
          <button onClick={() => { if (voiceOn) stop(); setVoiceOn(!voiceOn); }} data-testid="session-voice-toggle"
            className={`ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-md border ${voiceOn ? "border-[var(--aria-primary)] text-[var(--aria-primary-glow)]" : "border-white/15 text-slate-400"} hover:text-white`}>
            {voiceOn ? <Volume2 size={12} /> : <VolumeX size={12} />} {voiceOn ? "Voice on" : "Voice off"}
          </button>
          {voiceOn && interview.status === "active" && (
            <button onClick={replayQuestion} data-testid="replay-question-button" className="text-slate-400 hover:text-white">Replay</button>
          )}
        </div>

        {gapFlash && (
          <div data-testid="gap-flash" className={`aria-card p-5 mb-6 ring-glow-primary ${gapFlash.type === "gap_closed" ? "border-emerald-500/50" : "border-amber-500/50"}`}>
            <div className="flex items-center gap-2 mb-2">
              {gapFlash.type === "gap_detected" ? <TriangleAlert size={16} className="text-amber-400" /> : <CheckCircle2 size={16} className="text-emerald-400" />}
              <span className="text-xs uppercase tracking-[0.2em] font-semibold">
                {gapFlash.type === "gap_detected" ? "Knowledge gap detected" : "Gap closed"}
              </span>
            </div>
            <div className="font-display text-lg mb-2">{gapFlash.concept}</div>
            {gapFlash.type === "gap_closed" && <div className="text-sm text-slate-300">Understanding → Application</div>}
            {gapFlash.sources?.length > 0 && (
              <div className="mt-3 space-y-1.5">
                {gapFlash.sources.slice(0, 3).map((s, i) => {
                  const Icon = SOURCE_ICON[s.source_type] || FileText;
                  return (
                    <div key={`${s.source_type}-${s.source_id || s.title || i}`} className="flex items-center gap-2 text-xs text-slate-400" data-testid={`gap-source-${i}`}>
                      <Icon size={12} className="text-[var(--aria-primary-glow)]" />
                      <span className="uppercase tracking-wide">{s.source_type}</span>
                      <span>{s.source_title}</span>
                      {s.start_time != null && <span className="font-mono">{Math.floor(s.start_time/60)}:{String(Math.floor(s.start_time%60)).padStart(2,"0")}</span>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div className="space-y-4 mb-6">
          {interview.turns.map((t, i) => (
            <div key={t.timestamp || i} data-testid={`turn-${i}-${t.role}`} className={`flex ${t.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-lg px-4 py-3 text-sm leading-relaxed whitespace-pre-line ${
                t.role === "user" ? "bg-[var(--aria-primary)] text-white" : "bg-white/5 border border-white/10 text-slate-100"
              }`}>
                {t.content}
              </div>
            </div>
          ))}
        </div>

        {interview.status === "active" ? (
          <>
            <div className="flex gap-2 sticky bottom-6">
              <button onClick={recording ? stopRecording : startRecording} disabled={busy || transcribing} data-testid="interview-mic-button"
                title={recording ? "Stop and send" : "Answer by voice"}
                className={`px-4 rounded-md border flex items-center justify-center transition-colors disabled:opacity-40 ${
                  recording ? "bg-red-500/20 border-red-500 text-red-300 animate-pulse" : "bg-white/5 border-white/15 text-slate-300 hover:text-white"}`}>
                {transcribing ? <Loader2 size={16} className="animate-spin" /> : recording ? <Square size={16} /> : <Mic size={16} />}
              </button>
              <textarea value={answer} onChange={e => setAnswer(e.target.value)} rows={2}
                placeholder={recording ? "Listening… press stop to send" : "Type your answer, or use the mic…"}
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
                data-testid="interview-answer-input"
                className="flex-1 px-4 py-3 rounded-md bg-white/5 border border-white/15 outline-none focus:ring-2 focus:ring-[var(--aria-primary)] text-sm text-white resize-none" />
              <button onClick={() => submit()} disabled={busy || !answer.trim()} data-testid="interview-answer-submit"
                className="btn btn-primary px-4 disabled:opacity-40 flex items-center justify-center">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </button>
            </div>
            <button onClick={end} disabled={ending} data-testid="end-interview-button" className="mt-3 text-xs text-slate-500 hover:text-slate-300 flex items-center gap-1.5">
              {ending && <Loader2 size={11} className="animate-spin" />} {ending ? "Preparing your debrief…" : "End interview"}
            </button>
          </>
        ) : (
          <Debrief interview={interview} onRedrill={(c) => startDrill(c, navigate)} />
        )}
      </div>
    </div>
  );
}

async function startDrill(concept, navigate) {
  try {
    const { data } = await api.post("/interviews", { target: `Review: ${concept}`, topic_focus: [concept] });
    navigate(`/app/interviews/${data.interview_id}`);
  } catch { toast.error("Could not start drill"); }
}

async function downloadDebrief(interview) {
  try {
    const resp = await api.get(`/interviews/${interview.interview_id}/debrief.pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url; a.download = `aria-debrief-${(interview.target || "interview").replace(/[^a-z0-9]+/gi, "-").slice(0, 40)}.pdf`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Debrief PDF downloaded");
  } catch { toast.error("Could not export the debrief"); }
}

function EmailDebrief({ interview, onClose }) {
  const [to, setTo] = useState(localStorage.getItem("aria_advisor_email") || "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!to.includes("@")) { toast.error("Enter your advisor's email"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/interviews/${interview.interview_id}/debrief/email`, { to, note });
      localStorage.setItem("aria_advisor_email", to);
      toast.success(`Debrief sent to ${data.to}`);
      onClose(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send", { duration: 6000 }); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-4 rounded-md border border-[var(--aria-primary)]/40 bg-[var(--aria-primary)]/5 p-4 fade-in" data-testid="email-debrief">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--aria-primary-glow)] font-semibold">Send to my advisor</span>
        <button onClick={() => onClose(false)} data-testid="email-debrief-close" className="text-slate-400 hover:text-white"><X size={14} /></button>
      </div>
      <input value={to} onChange={e => setTo(e.target.value)} placeholder="advisor@university.edu" type="email" data-testid="email-debrief-to"
        className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/15 outline-none focus:ring-2 focus:ring-[var(--aria-primary)] text-sm text-white mb-2" />
      <textarea value={note} onChange={e => setNote(e.target.value)} rows={3} placeholder="A short personal note (optional) — e.g. what you'd like feedback on" data-testid="email-debrief-note"
        className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/15 outline-none focus:ring-2 focus:ring-[var(--aria-primary)] text-sm text-white resize-none mb-3" />
      <div className="flex items-center gap-3">
        <button onClick={send} disabled={busy} data-testid="email-debrief-send" className="btn btn-primary px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Send with PDF attached
        </button>
        <span className="text-[11px] text-slate-400">Replies go to your email. Until your sending domain is verified, Resend only delivers to your own inbox.</span>
      </div>
    </div>
  );
}

function ReplyBox({ comment, onSent }) {
  const [text, setText] = useState("");
  const [to, setTo] = useState(comment.email || "");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (text.trim().length < 2) return;
    if (!to.includes("@")) { toast.error("Add the advisor's email to send your reply"); return; }
    setBusy(true);
    try { const { data } = await api.post(`/advisor/comments/${comment.comment_id}/reply`, { text, to }); toast.success(`Reply emailed to ${data.to}`); onSent(data); setText(""); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send", { duration: 6000 }); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-2 pl-4 border-l border-white/10" data-testid={`reply-box-${comment.comment_id}`}>
      {!comment.email && (
        <input value={to} onChange={e => setTo(e.target.value)} type="email" placeholder="Advisor's email (they didn't leave one)" data-testid={`reply-to-${comment.comment_id}`}
          className="w-full mb-2 px-3 py-1.5 rounded-md bg-white/5 border border-white/15 outline-none focus:ring-2 focus:ring-[var(--aria-primary)] text-xs text-white" />
      )}
      <div className="flex gap-2">
        <textarea value={text} onChange={e => setText(e.target.value)} rows={2} placeholder="Write your reply — it's emailed to them with their comment quoted" data-testid={`reply-text-${comment.comment_id}`}
          className="flex-1 px-3 py-2 rounded-md bg-white/5 border border-white/15 outline-none focus:ring-2 focus:ring-[var(--aria-primary)] text-sm text-white resize-none" />
        <button onClick={send} disabled={busy || text.trim().length < 2} data-testid={`reply-send-${comment.comment_id}`} className="btn btn-primary px-3 disabled:opacity-40 flex items-center">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        </button>
      </div>
    </div>
  );
}

function AdvisorComments({ interviewId }) {
  const [comments, setComments] = useState(null);
  const [replying, setReplying] = useState(null);
  useEffect(() => {
    api.get(`/advisor/comments?interview_id=${interviewId}`).then(r => { setComments(r.data.comments); window.dispatchEvent(new Event("aria-comments-read")); }).catch(() => setComments([]));
  }, [interviewId]);
  if (!comments || comments.length === 0) return null;
  const onSent = (cid, reply) => setComments(cs => cs.map(c => c.comment_id === cid ? { ...c, replies: [...(c.replies || []), reply], email: reply.to } : c));
  return (
    <div className="mb-5 rounded-md border border-emerald-400/30 bg-emerald-400/5 p-4" data-testid="advisor-comments-panel">
      <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 font-semibold mb-2 inline-flex items-center gap-1.5"><MessageSquare size={11} /> Advisor comments · {comments.length}</div>
      <div className="space-y-3">
        {comments.map(c => (
          <div key={c.comment_id} className="text-sm" data-testid={`advisor-comment-${c.comment_id}`}>
            <div className="flex items-center justify-between text-xs text-slate-400 mb-0.5">
              <span className="inline-flex items-center gap-1.5">{c.source === "email" ? <Mail size={10} /> : <Link2 size={10} />} {c.author}{c.email ? <span className="text-slate-500">· {c.email}</span> : null}</span>
              <span>{new Date(c.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</span>
            </div>
            <div className="text-slate-100 whitespace-pre-line">{c.text}</div>
            {(c.replies || []).map(r => (
              <div key={r.reply_id} className="mt-1.5 pl-4 border-l border-[var(--aria-primary)]/50 text-slate-300" data-testid={`reply-${r.reply_id}`}>
                <div className="text-[11px] text-slate-500 inline-flex items-center gap-1"><CornerDownRight size={10} /> You replied · {new Date(r.at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</div>
                <div className="whitespace-pre-line">{r.text}</div>
              </div>
            ))}
            <button onClick={() => setReplying(replying === c.comment_id ? null : c.comment_id)} data-testid={`reply-button-${c.comment_id}`} className="mt-1.5 text-[11px] text-[var(--aria-primary-glow)] hover:underline inline-flex items-center gap-1"><Reply size={11} /> {replying === c.comment_id ? "Cancel" : "Reply by email"}</button>
            {replying === c.comment_id && <ReplyBox comment={c} onSent={(r) => { onSent(c.comment_id, r); setReplying(null); }} />}
          </div>
        ))}
      </div>
    </div>
  );
}

async function copyShareLink(interview) {
  try {
    const { data } = await api.get(`/interviews/${interview.interview_id}/share-link`);
    await navigator.clipboard?.writeText(data.share_url);
    toast.success("Private advisor link copied");
  } catch { toast.error("Could not create link"); }
}

function Debrief({ interview, onRedrill }) {
  const d = interview.debrief;
  const [emailing, setEmailing] = useState(false);
  const [shared, setShared] = useState(interview.shared_with || []);
  if (!d) return <div className="text-center py-6 text-slate-400 text-sm" data-testid="interview-completed-plain">Interview completed.</div>;
  const s = d.stats || {};
  return (
    <div className="aria-card p-6 ring-glow-primary" data-testid="interview-debrief">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-[var(--aria-primary-glow)]" />
          <span className="text-xs uppercase tracking-[0.2em] font-semibold">Debrief</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => downloadDebrief(interview)} data-testid="debrief-export-pdf"
            className="text-xs text-slate-300 hover:text-white inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-white/15 hover:border-[var(--aria-primary)]">
            <FileDown size={12} /> Export PDF
          </button>
          <button onClick={() => copyShareLink(interview)} data-testid="debrief-share-link"
            className="text-xs text-slate-300 hover:text-white inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-white/15 hover:border-[var(--aria-primary)]">
            <Link2 size={12} /> Copy link
          </button>
          <button onClick={() => setEmailing(!emailing)} data-testid="debrief-email-button"
            className={`text-xs inline-flex items-center gap-1.5 px-2.5 py-1 rounded border ${emailing ? "border-[var(--aria-primary)] text-[var(--aria-primary-glow)]" : "border-white/15 text-slate-300 hover:text-white hover:border-[var(--aria-primary)]"}`}>
            <Mail size={12} /> Email my advisor
          </button>
        </div>
      </div>
      {emailing && <EmailDebrief interview={interview} onClose={(sent) => { setEmailing(false); if (sent) setShared(s => [...s, { at: new Date().toISOString() }]); }} />}
      {shared.length > 0 && <div className="text-[11px] text-slate-400 mb-3" data-testid="debrief-shared">Shared {shared.length} time{shared.length === 1 ? "" : "s"}{shared[shared.length - 1]?.to ? ` · last to ${shared[shared.length - 1].to}` : ""}</div>}
      <AdvisorComments interviewId={interview.interview_id} />
      <p className="text-[15px] leading-relaxed text-slate-100 mb-5" data-testid="debrief-summary">{d.summary}</p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <DStat label="Answered" value={s.questions_answered ?? 0} />
        <DStat label="Concepts" value={s.concepts_covered ?? 0} />
        <DStat label="Gaps closed" value={`${s.gaps_closed ?? 0}/${s.gaps_found ?? 0}`} />
        <DStat label="Mastery gained" value={`+${s.levels_gained ?? 0}`} accent />
      </div>

      {d.mastery_changes?.length > 0 && (
        <div className="mb-5" data-testid="debrief-mastery-changes">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold mb-2 flex items-center gap-1.5"><TrendingUp size={12} /> Mastery changes</div>
          <div className="space-y-1.5">
            {d.mastery_changes.map(c => (
              <div key={c.concept} className="flex items-center justify-between text-sm px-3 py-2 rounded-md bg-white/5" data-testid={`mastery-change-${c.concept}`}>
                <span>{c.concept}</span>
                <span className="font-mono text-xs text-slate-300">
                  {LEVEL_LABEL[c.before]} → <span className={c.delta > 0 ? "text-emerald-400" : "text-slate-100"}>{LEVEL_LABEL[c.after]}</span>
                  {c.delta > 0 && <span className="text-emerald-400 ml-1">+{c.delta}</span>}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4 mb-5">
        {d.strengths?.length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-semibold mb-2">Strengths</div>
            <ul className="text-sm text-slate-300 space-y-1 list-disc pl-4">{d.strengths.map((x) => <li key={x}>{x}</li>)}</ul>
          </div>
        )}
        {d.focus_next?.length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-amber-400 font-semibold mb-2">Focus next</div>
            <ul className="text-sm text-slate-300 space-y-1 list-disc pl-4">{d.focus_next.map((x) => <li key={x}>{x}</li>)}</ul>
          </div>
        )}
      </div>

      <div className="text-sm text-slate-200 border-l-2 border-[var(--aria-primary)] pl-3 mb-5" data-testid="debrief-recommendation">{d.recommendation}</div>

      {(d.reminders_created?.length > 0 || d.plan_tasks_completed > 0) && (
        <div className="text-xs text-slate-400 mb-5 flex flex-wrap gap-x-4 gap-y-1" data-testid="debrief-automation">
          {d.reminders_created?.length > 0 && (
            <span className="inline-flex items-center gap-1.5"><CalendarClock size={12} className="text-[var(--aria-accent)]" />
              {d.reminders_created.length} spaced re-drill reminder{d.reminders_created.length === 1 ? "" : "s"} added to Calendar ({[...new Set(d.reminders_created.map(r => r.concept))].join(", ")})</span>
          )}
          {d.plan_tasks_completed > 0 && (
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={12} className="text-emerald-400" /> {d.plan_tasks_completed} countdown task{d.plan_tasks_completed === 1 ? "" : "s"} ticked off</span>
          )}
        </div>
      )}

      {(d.drill_concepts?.[0] || d.focus_next?.[0]) && (
        <button onClick={() => onRedrill(d.drill_concepts?.[0] || d.focus_next[0])} data-testid="debrief-redrill-button"
          className="btn btn-primary px-4 py-2 text-sm inline-flex items-center gap-2">
          Drill {d.drill_concepts?.[0] || d.focus_next[0]} now <ArrowRight size={14} />
        </button>
      )}
    </div>
  );
}

function DStat({ label, value, accent }) {
  return (
    <div className="rounded-md bg-white/5 px-3 py-2.5">
      <div className={`font-display text-2xl ${accent ? "text-emerald-400" : ""}`}>{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-slate-400">{label}</div>
    </div>
  );
}

export function InterviewSessionPage() {
  const { id } = useParams();
  return <InterviewSession id={id} />;
}
